<script setup lang="ts">
  import { EmployeeWithOrganizationDto } from '@/services/api/employee-api.ts';
  import { useEmployeeStore } from '@/stores/employee.ts';
  import { useThemeStore } from '@/stores/theme';
  import { reactive, ref, watch } from 'vue';

  const themeStore = useThemeStore();
  const employeeStore = useEmployeeStore();

  const selected = ref<Map<number, EmployeeWithOrganizationDto>>(
    new Map<number, EmployeeWithOrganizationDto>()
  );
  const employees = ref<EmployeeWithOrganizationDto[]>([]);

  const emit = defineEmits<{
    (e: 'on-change', item: EmployeeWithOrganizationDto[]): void;
  }>();

  watch(
    () => selected.value.size,
    (_) => {
      emit('on-change', Array.from(selected.value.values()));
    }
  );
  const search = reactive<{
    loading: boolean;
    searchIdx: number;
    searchTimer: number | undefined;
  }>({
    loading: false,
    searchIdx: 0,
    searchTimer: undefined,
  });

  function request(reqIndex: number, keyword: string) {
    search.loading = true;

    return window.setTimeout(async () => {
      const items = await employeeStore.searchAsync(keyword);
      if (reqIndex < search.searchIdx) {
        return;
      }

      employees.value = items;
      search.loading = false;
      search.searchTimer = undefined;
    }, 500);
  }

  function searchByKeyword(input: Event) {
    if (search.searchTimer) {
      clearTimeout(search.searchTimer);
      search.searchTimer = undefined;
    }
    if (!input.target) return;

    const keyword = (input.target as HTMLInputElement).value;
    const pattern = /([^가-힣a-zA-Z0-9@\\.\x20])/i;

    if (pattern.test(keyword)) return;

    if (keyword && keyword.length >= 2) {
      search.searchTimer = request(++search.searchIdx, keyword);
    } else {
      employees.value = [];
    }
  }
</script>

<template>
  <div class="dropdown w-full">
    <input
      type="text"
      placeholder="사원 이름을 입력해주세요."
      class="input input-bordered w-full"
      tabindex="0"
      @input="searchByKeyword" />
    <ul
      v-if="employees.length > 0"
      tabindex="0"
      class="menu menu-compact border dropdown-content z-[100] bg-base-200 top-14 max-h-96 rounded-md"
      :class="{
        'border-slate-700': themeStore.isDark,
      }">
      <li
        v-for="(employee, index) in employees"
        :key="employee.no"
        class="w-full"
        :class="{
          'border-b': index < employees.length - 1,
          'border-b-base-content/10': index < employees.length - 1,
        }">
        <a
          class="text-md p-3"
          @click="
            selected.has(employee.no)
              ? selected.delete(employee.no)
              : selected.set(employee.no, employee)
          ">
          <input
            type="checkbox"
            :disabled="true"
            :checked="selected.has(employee.no)"
            class="checkbox checkbox-xs" />
          <h3 class="font-semibold">{{ employee.name }}</h3>
          <div
            class="text-gray-700"
            :class="{
              'text-gray-300': themeStore.isDark,
            }">
            ({{ employee.organization.name }})
          </div>
        </a>
      </li>
    </ul>
  </div>

  <div
    v-if="selected.size != 0"
    class="mt-3">
    <div
      v-for="[no, employee] in selected"
      :key="no"
      class="badge badge-info gap-2 p-3 m-1">
      <h3 class="font-semibold">{{ employee.name }}</h3>
      <div class="text-gray-700">({{ employee.organization.name }})</div>
      <button
        class="btn btn-circle btn-info btn-xs"
        @click="selected.delete(employee.no)">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  </div>
</template>
